import { CATALOG_ICON_WEB_SEARCH_TOGGLE_CODE } from '@orbind/core'
import { Button, FieldBlock, Input, Modal, useToast } from '@orbind/ui'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'
import { api, apiBase, apiOrigin, webTokenStorage } from '../lib/api.ts'
import { useFeatureToggle } from '../lib/feature-toggle.ts'
import './icon-picker.css'

/**
 * Escolha do icone de um catalogo, em tres origens:
 *
 * 1. **Biblioteca** — os ~1450 icones do Font Awesome ja carregado na pagina.
 *    A lista e lida da propria folha de estilo em runtime (ver `lerBiblioteca`),
 *    em vez de ficar embutida no bundle: nao ha lista para manter e ela
 *    acompanha sozinha a versao do Font Awesome.
 * 2. **Meus ícones** — os que ja foram copiados para esta instalacao.
 * 3. **Do computador** — sobe o arquivo e guarda a FK do registro.
 * 4. **Da internet** — busca por palavra e COPIA o escolhido para a base, para
 *    o icone nao depender de um site de terceiro continuar no ar.
 *
 * Biblioteca devolve a classe (`fas fa-bug`) por `onChange`; arquivo e
 * internet devolvem o ID do registro por `onUpload` — nunca a URL.
 */

type Origem = 'biblioteca' | 'meus' | 'arquivo' | 'internet'

/** Estilos do Font Awesome 5 free — `fab` so vale para as marcas. */
const ESTILOS = [
  { valor: 'fas', rotulo: 'Sólido' },
  { valor: 'far', rotulo: 'Contorno' },
  { valor: 'fab', rotulo: 'Marcas' },
] as const

export function ehUrlDeImagem(valor: string) {
  return /^(https?:\/\/|\/public\/|data:image\/)/.test(valor.trim())
}

/**
 * Resolve o caminho de um icone servido pela API.
 *
 * `/public/catalog-icon/<id>` e relativo, e em desenvolvimento o front roda em
 * :5173 enquanto a API esta em :3333 — sem prefixar a origem, o navegador
 * pediria a imagem ao servidor errado e ela apareceria quebrada. Em producao
 * `apiOrigin()` e a propria origem, entao o resultado nao muda.
 */
export function urlDoIcone(caminho: string) {
  return caminho.startsWith('/public/') ? `${apiOrigin()}${caminho}` : caminho
}

/**
 * Desenha o icone de um catalogo, seja ele classe do Font Awesome ou imagem.
 *
 * Use SEMPRE isto em vez de `<i className={icon}>`: desde que o cadastro
 * aceita arquivo e endereco, `icon` pode ser uma URL, e a tag `<i>` nao
 * mostraria nada.
 */
export function CatalogIcon({
  icon,
  fallback = 'fas fa-shapes',
  size,
  className,
  fill,
}: {
  icon: string | null | undefined
  fallback?: string
  size?: number
  className?: string
  /** Imagem preenche o container (100%) em vez de usar `size`; o CSS do pai
   *  define o tamanho. Icones de fonte continuam usando `size`. */
  fill?: boolean
}) {
  const valor = (icon ?? '').trim()
  if (valor !== '' && ehUrlDeImagem(valor)) {
    return (
      <img
        src={urlDoIcone(valor)}
        alt=""
        className={className}
        style={
          fill
            ? { width: '100%', height: '100%', objectFit: 'contain', display: 'block' }
            : { width: size ?? '1em', height: size ?? '1em', objectFit: 'contain', display: 'block' }
        }
      />
    )
  }
  return (
    <i
      className={`${valor === '' ? fallback : valor} ${className ?? ''}`.trim()}
      aria-hidden
      style={size ? { fontSize: size } : undefined}
    />
  )
}

/**
 * Nomes de icone lidos das regras `.fa-xxx::before` da folha do Font Awesome.
 *
 * So funciona porque o CDN responde com CORS; sem isso `cssRules` lanca
 * SecurityError. Se um dia falhar, a lista volta vazia e o campo continua
 * aceitando a classe digitada — a tela nao quebra.
 */
function lerBiblioteca(): string[] {
  const nomes = new Set<string>()
  for (const folha of Array.from(document.styleSheets)) {
    let regras: CSSRuleList
    try {
      regras = folha.cssRules
    } catch {
      continue // folha de outra origem sem CORS
    }
    for (const regra of Array.from(regras)) {
      const seletor = (regra as CSSStyleRule).selectorText
      if (!seletor || !seletor.includes('.fa-')) continue
      for (const achado of seletor.matchAll(/\.fa-([a-z0-9-]+)::?before/g)) {
        if (achado[1]) nomes.add(achado[1])
      }
    }
  }
  return [...nomes].sort()
}

/**
 * Campo do formulario: mostra o icone atual e um botao que abre o seletor
 * numa modal. A escolha so vale ao confirmar — cancelar descarta.
 */
export function IconField({
  value,
  imageId,
  onChange,
  label = 'Ícone',
  color,
}: {
  value: string
  imageId?: string | null
  onChange: (escolha: { icon: string; iconImageId: string | null }) => void
  label?: string
  color?: string
}) {
  const [aberto, setAberto] = useState(false)
  /** Onde o campo esta na tela — a caixa flutuante se ancora aqui. */
  const [anc, setAnc] = useState<DOMRect | null>(null)
  const atual = imageId ? `/public/catalog-icon/${imageId}` : value

  return (
    <FieldBlock label={label} hint="Escolha da biblioteca, envie um arquivo ou informe um endereço.">
      {() => (
        <div className="icp__campo">
          <span className="icp__preview" style={color ? { color } : undefined} aria-hidden>
            {atual === '' ? <span className="icp__vazio">sem ícone</span> : <CatalogIcon icon={atual} size={24} />}
          </span>
          <Button
            variant="secondary"
            icon="fas fa-icons"
            onClick={(e) => {
              setAnc((e.currentTarget as HTMLElement).closest('.icp__campo')?.getBoundingClientRect() ?? null)
              setAberto(true)
            }}
          >
            Selecionar ícone
          </Button>
          {atual !== '' ? (
            <Button variant="ghost" onClick={() => onChange({ icon: '', iconImageId: null })}>
              Remover
            </Button>
          ) : null}

          {aberto ? (
            <IconPickerPop
              anc={anc}
              value={value}
              imageId={imageId ?? null}
              onClose={() => setAberto(false)}
              onConfirm={(escolha) => {
                onChange(escolha)
                setAberto(false)
              }}
            />
          ) : null}
        </div>
      )}
    </FieldBlock>
  )
}

/**
 * Lista de icones FLUTUANTE, desenhada no body (2026-09-01): dentro do modal
 * ela era recortada pelo scroll e pintada por baixo do rodape — a caixa tem
 * que ficar sobre todas as janelas. Ancora no campo, prefere abrir para
 * baixo, vira para cima quando nao cabe e nunca escapa da tela. Fecha no Esc
 * ou clicando fora.
 */
function IconPickerPop({
  anc,
  value,
  imageId,
  onClose,
  onConfirm,
}: {
  anc: DOMRect | null
  value: string
  imageId: string | null
  onClose: () => void
  onConfirm: (escolha: { icon: string; iconImageId: string | null }) => void
}) {
  const [rascunhoIcone, setRascunhoIcone] = useState(value)
  const [rascunhoImagem, setRascunhoImagem] = useState<string | null>(imageId)
  const caixa = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)

  useLayoutEffect(() => {
    const el = caixa.current
    if (!el) return
    const medir = () => {
      const r = el.getBoundingClientRect()
      const m = 12
      let left = anc ? anc.left : (window.innerWidth - r.width) / 2
      let top = anc ? anc.bottom + 8 : (window.innerHeight - r.height) / 2
      if (anc && top + r.height > window.innerHeight - m) top = anc.top - r.height - 8
      left = Math.min(Math.max(m, left), Math.max(m, window.innerWidth - r.width - m))
      top = Math.min(Math.max(m, top), Math.max(m, window.innerHeight - r.height - m))
      setPos({ left, top })
    }
    medir()
    // a lista de icones carrega DEPOIS e a caixa cresce: reposiciona junto,
    // senao o clamp inicial deixava o rodape para fora da tela. Os timers
    // cobrem ambientes em que o ResizeObserver nao dispara (aba escondida).
    const ro = new ResizeObserver(medir)
    ro.observe(el)
    window.addEventListener('resize', medir)
    const timers = [250, 700, 1500].map((ms) => window.setTimeout(medir, ms))
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', medir)
      timers.forEach(clearTimeout)
    }
  }, [anc])

  useEffect(() => {
    const foraOuEsc = (e: Event) => {
      if (e.type === 'keydown' && (e as KeyboardEvent).key !== 'Escape') return
      if (e.type === 'pointerdown' && caixa.current?.contains(e.target as Node)) return
      onClose()
    }
    document.addEventListener('pointerdown', foraOuEsc)
    document.addEventListener('keydown', foraOuEsc)
    return () => {
      document.removeEventListener('pointerdown', foraOuEsc)
      document.removeEventListener('keydown', foraOuEsc)
    }
  }, [onClose])

  return createPortal(
    <div
      className="icp__pop"
      ref={caixa}
      style={{ left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden' }}
    >
      <IconPicker
        value={rascunhoImagem ? `/public/catalog-icon/${rascunhoImagem}` : rascunhoIcone}
        onChange={(valor) => {
          setRascunhoIcone(valor)
          setRascunhoImagem(null)
        }}
        onUpload={(id) => {
          setRascunhoImagem(id)
          setRascunhoIcone('')
        }}
      />
      <div className="icp__poprodape">
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={() => onConfirm({ icon: rascunhoImagem ? '' : rascunhoIcone, iconImageId: rascunhoImagem })}
        >
          Usar este ícone
        </Button>
      </div>
    </div>,
    document.body,
  )
}

/** Modal com as tres origens; confirma ou descarta. */
function IconPickerModal({
  value,
  imageId,
  onClose,
  onConfirm,
}: {
  value: string
  imageId: string | null
  onClose: () => void
  onConfirm: (escolha: { icon: string; iconImageId: string | null }) => void
}) {
  const [rascunhoIcone, setRascunhoIcone] = useState(value)
  const [rascunhoImagem, setRascunhoImagem] = useState<string | null>(imageId)

  return (
    <Modal
      open
      onClose={onClose}
      title="Selecionar ícone"
      width={620}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm({ icon: rascunhoImagem ? '' : rascunhoIcone, iconImageId: rascunhoImagem })}
          >
            Usar este ícone
          </Button>
        </>
      }
    >
      <IconPicker
        value={rascunhoImagem ? `/public/catalog-icon/${rascunhoImagem}` : rascunhoIcone}
        onChange={(valor) => {
          setRascunhoIcone(valor)
          setRascunhoImagem(null)
        }}
        onUpload={(id) => setRascunhoImagem(id)}
        label=""
      />
    </Modal>
  )
}

export function IconPicker({
  value,
  onChange,
  onUpload,
  label = 'Ícone',
}: {
  value: string
  onChange: (valor: string) => void
  /** Recebe o ID do arquivo enviado — a FK, nao a URL. */
  onUpload?: (iconImageId: string) => void
  label?: string
}) {
  const toast = useToast()
  // Feature toggle (kill-switch): com a busca externa desligada, a aba some.
  // So esconder nao bastaria — a rota tambem esta atras de requireToggle na API.
  const buscaWebLigada = useFeatureToggle(CATALOG_ICON_WEB_SEARCH_TOGGLE_CODE)
  const [origem, setOrigem] = useState<Origem>(() => (ehUrlDeImagem(value) ? 'meus' : 'biblioteca'))
  const [busca, setBusca] = useState('')
  const [estilo, setEstilo] = useState<(typeof ESTILOS)[number]['valor']>('fas')
  const [biblioteca, setBiblioteca] = useState<string[]>([])
  const [enviando, setEnviando] = useState(false)
  const arquivoRef = useRef<HTMLInputElement>(null)
  // Arquivo selecionado aguardando recorte (crop com zoom) antes do upload.
  const [recorte, setRecorte] = useState<File | null>(null)

  // busca na internet + biblioteca propria
  const [buscaWeb, setBuscaWeb] = useState('')
  const [resultados, setResultados] = useState<{ key: string; set: string; name: string; url: string }[]>([])
  const [buscando, setBuscando] = useState(false)
  const [importando, setImportando] = useState<string | null>(null)
  const [meus, setMeus] = useState<{ id: string; name: string; url: string }[]>([])

  /** Recarrega "Meus ícones" — chamado ao abrir e depois de cada cópia. */
  const carregarMeus = async () => {
    try {
      const resposta = await api.catalog.icons.$get()
      const dados = (await resposta.json()) as { icons?: { id: string; name: string; url: string }[] }
      setMeus(dados.icons ?? [])
    } catch {
      /* sem icones proprios ainda; a aba mostra o estado vazio */
    }
  }

  useEffect(() => {
    void carregarMeus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const buscarNaWeb = async () => {
    const termo = buscaWeb.trim()
    if (termo.length < 2) {
      toast.push({ tone: 'info', message: 'Digite ao menos duas letras para buscar.' })
      return
    }
    setBuscando(true)
    try {
      const resposta = await api.catalog.icons.search.$get({ query: { q: termo } })
      const dados = (await resposta.json()) as {
        icons?: { key: string; set: string; name: string; url: string }[]
        error?: { message?: string }
      }
      if (!resposta.ok) throw new Error(dados.error?.message ?? 'Busca indisponível.')
      setResultados(dados.icons ?? [])
      if ((dados.icons ?? []).length === 0) {
        toast.push({ tone: 'info', message: `Nenhum ícone encontrado para “${termo}”.` })
      }
    } catch (erro) {
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Busca indisponível.' })
    } finally {
      setBuscando(false)
    }
  }

  /** Copia para a base local: o icone deixa de depender do site de origem. */
  const importarDaWeb = async (url: string) => {
    setImportando(url)
    try {
      const resposta = await api.catalog.icons.import.$post({ json: { url } })
      const dados = (await resposta.json()) as { id?: string; url?: string; error?: { message?: string } }
      if (!resposta.ok || !dados.id || !dados.url) throw new Error(dados.error?.message ?? 'Não foi possível copiar.')
      if (onUpload) onUpload(dados.id)
      else onChange(dados.url)
      await carregarMeus()
      setOrigem('meus')
      toast.push({ tone: 'success', message: 'Ícone copiado para “Meus ícones”.' })
    } catch (erro) {
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível copiar.' })
    } finally {
      setImportando(null)
    }
  }

  // se o kill-switch desligar com a aba aberta, volta para a biblioteca
  useEffect(() => {
    if (!buscaWebLigada && origem === 'internet') setOrigem('biblioteca')
  }, [buscaWebLigada, origem])

  // a folha do Font Awesome pode nao ter chegado no primeiro render
  useEffect(() => {
    const carregar = () => setBiblioteca(lerBiblioteca())
    carregar()
    if (document.readyState !== 'complete') {
      window.addEventListener('load', carregar, { once: true })
      return () => window.removeEventListener('load', carregar)
    }
    return undefined
  }, [])

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    const base = termo === '' ? biblioteca : biblioteca.filter((n) => n.includes(termo))
    // a grade nao precisa de milhares de nos para o usuario achar o que quer
    return base.slice(0, 300)
  }, [biblioteca, busca])

  const selecionarArquivo = async (arquivo: File) => {
    setEnviando(true)
    try {
      const corpo = new FormData()
      corpo.append('file', arquivo)
      // fetch direto (nao o cliente hono): passar um FormData pelo `$post({form})`
      // nao anexa o arquivo (o cliente itera Object.entries, que ignora FormData),
      // entao o servidor recebia o campo vazio e recusava com "Envie um arquivo".
      const resposta = await fetch(`${apiBase()}/catalog/icons`, {
        method: 'POST',
        headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
        body: corpo,
      })
      const dados = (await resposta.json()) as { id?: string; url?: string }
      if (!resposta.ok || !dados.id || !dados.url) throw new Error('Não foi possível enviar o ícone.')
      // guarda a FK; a URL serve so para a previa desta tela
      if (onUpload) onUpload(dados.id)
      else onChange(dados.url)
      await carregarMeus()
      setOrigem('meus')
      toast.push({ tone: 'success', message: 'Ícone enviado.' })
    } catch (erro) {
      toast.push({
        tone: 'danger',
        message: erro instanceof Error ? erro.message : 'Falha ao enviar o ícone.',
      })
    } finally {
      setEnviando(false)
    }
  }

  const classeAtual = ehUrlDeImagem(value) ? '' : value

  return (
    <FieldBlock label={label} hint="Escolha da biblioteca, envie um arquivo ou informe um endereço.">
      {() => (
      <>
      <div className="icp">
        <div className="icp__preview" aria-label="Pré-visualização do ícone">
          {value === '' ? (
            <span className="icp__vazio">sem ícone</span>
          ) : ehUrlDeImagem(value) ? (
            <img src={value} alt="" />
          ) : (
            <i className={value} aria-hidden />
          )}
        </div>

        <div className="icp__corpo">
          <div className="icp__abas" role="tablist" aria-label="Origem do ícone">
            {(
              [
                ['biblioteca', 'Biblioteca'],
                ['meus', 'Meus ícones'],
                ['arquivo', 'Do computador'],
                ...(buscaWebLigada ? ([['internet', 'Da internet']] as const) : []),
              ] as const
            ).map(([chave, rotulo]) => (
              <button
                key={chave}
                type="button"
                role="tab"
                aria-selected={origem === chave}
                className={`icp__aba${origem === chave ? ' icp__aba--on' : ''}`}
                onClick={() => setOrigem(chave)}
              >
                {rotulo}
              </button>
            ))}
          </div>

          {origem === 'biblioteca' ? (
            <>
              <div className="icp__filtros">
                <Input
                  value={busca}
                  placeholder={`Buscar entre ${biblioteca.length} ícones…`}
                  aria-label="Buscar ícone"
                  onChange={(e) => setBusca(e.target.value)}
                />
                <div className="icp__estilos" role="group" aria-label="Estilo do ícone">
                  {ESTILOS.map((op) => (
                    <button
                      key={op.valor}
                      type="button"
                      aria-pressed={estilo === op.valor}
                      className={`icp__estilo${estilo === op.valor ? ' icp__estilo--on' : ''}`}
                      onClick={() => setEstilo(op.valor)}
                    >
                      {op.rotulo}
                    </button>
                  ))}
                </div>
              </div>

              {biblioteca.length === 0 ? (
                <p className="icp__aviso">
                  Não foi possível ler a biblioteca de ícones. Digite a classe direto no campo abaixo.
                </p>
              ) : filtrados.length === 0 ? (
                <p className="icp__aviso">Nenhum ícone encontrado para “{busca}”.</p>
              ) : (
                <div className="icp__grade">
                  {filtrados.map((nome) => {
                    const classe = `${estilo} fa-${nome}`
                    return (
                      <button
                        key={nome}
                        type="button"
                        className={`icp__item${classeAtual === classe ? ' icp__item--on' : ''}`}
                        title={nome}
                        aria-label={nome}
                        aria-pressed={classeAtual === classe}
                        onClick={() => onChange(classe)}
                      >
                        <i className={classe} aria-hidden />
                      </button>
                    )
                  })}
                </div>
              )}

              <Input
                value={classeAtual}
                placeholder="ou digite a classe: fas fa-bug"
                aria-label="Classe do ícone"
                onChange={(e) => onChange(e.target.value)}
              />
            </>
          ) : origem === 'arquivo' ? (
            <div className="icp__envio">
              <input
                ref={arquivoRef}
                type="file"
                accept="image/png,image/svg+xml,image/jpeg,image/webp,image/gif"
                hidden
                onChange={(e) => {
                  const arquivo = e.target.files?.[0]
                  e.target.value = ''
                  if (!arquivo) return
                  // SVG e vetor: nao recorta em canvas, sobe direto. O resto abre
                  // o recortador (crop com zoom) antes do upload.
                  if (arquivo.type === 'image/svg+xml') void selecionarArquivo(arquivo)
                  else setRecorte(arquivo)
                }}
              />
              <Button
                variant="secondary"
                icon="fas fa-upload"
                disabled={enviando}
                onClick={() => arquivoRef.current?.click()}
              >
                {enviando ? 'Enviando…' : 'Escolher arquivo'}
              </Button>
              <p className="icp__aviso">PNG, SVG, JPG, WebP ou GIF, até 512 KB.</p>
            </div>
          ) : origem === 'meus' ? (
            meus.length === 0 ? (
              <p className="icp__aviso">
                Nenhum ícone próprio ainda. Envie um arquivo ou traga um da internet — ele fica guardado aqui.
              </p>
            ) : (
              <div className="icp__grade">
                {meus.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`icp__item${value === item.url ? ' icp__item--on' : ''}`}
                    title={item.name}
                    aria-label={item.name}
                    onClick={() => onUpload?.(item.id) ?? onChange(item.url)}
                  >
                    <img src={urlDoIcone(item.url)} alt="" />
                  </button>
                ))}
              </div>
            )
          ) : (
            <div className="icp__envio">
              <Input
                value={buscaWeb}
                placeholder="Buscar ícones na internet: bug, empresa, relatório…"
                aria-label="Buscar ícones na internet"
                onChange={(e) => setBuscaWeb(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void buscarNaWeb()
                  }
                }}
              />
              <Button variant="secondary" icon="fas fa-search" disabled={buscando} onClick={() => void buscarNaWeb()}>
                {buscando ? 'Buscando…' : 'Buscar'}
              </Button>

              {resultados.length > 0 ? (
                <div className="icp__grade icp__grade--web">
                  {resultados.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className="icp__item"
                      title={`${item.name} (${item.set})`}
                      aria-label={item.name}
                      disabled={importando !== null}
                      onClick={() => void importarDaWeb(item.url)}
                    >
                      {importando === item.url ? (
                        <i className="fas fa-spinner fa-spin" aria-hidden />
                      ) : (
                        <img src={item.url} alt="" />
                      )}
                    </button>
                  ))}
                </div>
              ) : null}

              <p className="icp__aviso">
                Ao escolher, o ícone é copiado para “Meus ícones” — ele passa a viver aqui e não depende mais do site
                de origem.
              </p>
            </div>
          )}
        </div>
      </div>
      {recorte ? (
        <RecortadorImagem
          arquivo={recorte}
          onCancelar={() => setRecorte(null)}
          onConfirmar={(arquivoRecortado) => {
            setRecorte(null)
            void selecionarArquivo(arquivoRecortado)
          }}
        />
      ) : null}
      </>
      )}
    </FieldBlock>
  )
}

/**
 * Recortador de imagem com zoom: mostra a imagem num quadro quadrado, deixa
 * arrastar (pan) e dar zoom (slider/roda), e devolve um PNG quadrado do que
 * ficou dentro do quadro. Sem dependencia externa — canvas + matematica.
 */
export function RecortadorImagem({
  arquivo,
  onCancelar,
  onConfirmar,
  titulo = 'Recortar imagem',
  rotuloConfirmar = 'Recortar e usar',
  redondo = false,
  saida = 256,
}: {
  arquivo: File
  onCancelar: () => void
  onConfirmar: (arquivo: File) => void
  titulo?: string
  rotuloConfirmar?: string
  /** Máscara redonda por cima: mostra como a foto aparece num avatar (grupo do chat). */
  redondo?: boolean
  /** Lado do PNG gerado. */
  saida?: number
}) {
  const LADO = 280 // lado do quadro de recorte (px na tela)
  const SAIDA = saida
  // A URL nasce e morre no MESMO efeito: criada no useState e revogada no
  // cleanup, o StrictMode revogava antes da imagem carregar (imagem quebrada).
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    const criada = URL.createObjectURL(arquivo)
    setUrl(criada)
    return () => URL.revokeObjectURL(criada)
  }, [arquivo])
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null)
  const [zoom, setZoom] = useState(1) // multiplicador acima do "cobrir"
  const [pos, setPos] = useState({ x: 0, y: 0 }) // canto sup-esq da imagem no quadro
  const arraste = useRef<{ x: number; y: number; px: number; py: number } | null>(null)


  // escala base = COBRIR o quadro; a efetiva multiplica pelo zoom
  const base = nat ? Math.max(LADO / nat.w, LADO / nat.h) : 1
  const escala = base * zoom
  const imgW = nat ? nat.w * escala : LADO
  const imgH = nat ? nat.h * escala : LADO

  // mantem a imagem sempre cobrindo o quadro (sem faixa vazia)
  const limitar = (p: { x: number; y: number }) => ({
    x: Math.min(0, Math.max(LADO - imgW, p.x)),
    y: Math.min(0, Math.max(LADO - imgH, p.y)),
  })

  useEffect(() => {
    // centraliza / re-limita quando a imagem carrega ou o zoom muda
    setPos((p) => limitar({ x: (LADO - imgW) / 2 || p.x, y: (LADO - imgH) / 2 || p.y }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nat, zoom])

  const aoArrastar = (e: ReactPointerEvent) => {
    if (!arraste.current) return
    const d = arraste.current
    setPos(limitar({ x: d.px + (e.clientX - d.x), y: d.py + (e.clientY - d.y) }))
  }

  const confirmar = () => {
    if (!nat || !url) return
    const canvas = document.createElement('canvas')
    canvas.width = SAIDA
    canvas.height = SAIDA
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    // regiao da imagem-fonte visivel no quadro
    const sx = -pos.x / escala
    const sy = -pos.y / escala
    const sLado = LADO / escala
    const img = new Image()
    img.onload = () => {
      ctx.drawImage(img, sx, sy, sLado, sLado, 0, 0, SAIDA, SAIDA)
      canvas.toBlob((blob) => {
        if (!blob) return
        const nome = arquivo.name.replace(/\.[^.]+$/, '') + '.png'
        onConfirmar(new File([blob], nome, { type: 'image/png' }))
      }, 'image/png')
    }
    img.src = url
  }

  return (
    <div className="icp-crop">
      <div className="icp-crop__scrim" onClick={onCancelar} aria-hidden />
      <div className="icp-crop__caixa" role="dialog" aria-label={titulo}>
        <h3 className="icp-crop__titulo">{titulo}</h3>
        <div
          className={`icp-crop__palco${redondo ? ' icp-crop__palco--redondo' : ''}`}
          style={{ width: LADO, height: LADO }}
          onPointerDown={(e) => {
            ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
            arraste.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y }
          }}
          onPointerMove={aoArrastar}
          onPointerUp={() => (arraste.current = null)}
          onWheel={(e) => setZoom((z) => Math.min(5, Math.max(1, z - e.deltaY * 0.001)))}
        >
          {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
          {url ? (
          <img
            src={url}
            alt=""
            draggable={false}
            onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            style={{
              position: 'absolute',
              left: pos.x,
              top: pos.y,
              width: imgW,
              height: imgH,
              maxWidth: 'none',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
          ) : null}
        </div>
        <label className="icp-crop__zoom">
          <i className="fas fa-search-minus" aria-hidden />
          <input
            type="range"
            min={1}
            max={5}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            aria-label="Zoom"
          />
          <i className="fas fa-search-plus" aria-hidden />
        </label>
        <div className="icp-crop__acoes">
          <Button variant="ghost" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={confirmar} disabled={!nat}>
            {rotuloConfirmar}
          </Button>
        </div>
      </div>
    </div>
  )
}
